import { useState, useRef } from 'react';
import { LineType, PolygonType } from 'klinecharts';
import type { DeepPartial, OverlayStyle, OverlayCreate } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { Modal } from '@/components/ui/Modal';
import { Z_INDEX } from '@/constants/zIndex';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DEFAULT_COLOR = '#2196F3';

function colorToHex(color: string | CanvasGradient | undefined): string {
  if (!color || typeof color !== 'string') return DEFAULT_COLOR;
  if (color.startsWith('#')) {
    return color.length === 4
      ? '#' + color.slice(1).split('').map((c) => c + c).join('')
      : color.slice(0, 7);
  }
  const m = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!m) return DEFAULT_COLOR;
  return '#' + [m[1], m[2], m[3]].map((n) => parseInt(n).toString(16).padStart(2, '0')).join('');
}

function colorToOpacity(color: string | CanvasGradient | undefined): number {
  if (!color || typeof color !== 'string') return 20;
  const m = color.match(/rgba\(.+,\s*([\d.]+)\)/);
  if (!m) return 100;
  return Math.round(parseFloat(m[1]) * 100);
}

function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${(opacity / 100).toFixed(2)})`;
}

interface StyleState {
  lineColor:   string;
  lineWidth:   number;
  lineStyle:   LineType;
  hasFill:     boolean;
  fillColor:   string;
  fillOpacity: number;
  borderColor: string;
  borderWidth: number;
  borderStyle: LineType;
}

function buildOverlayStyle(s: StyleState): DeepPartial<OverlayStyle> {
  const fillRgba    = hexToRgba(s.fillColor, s.fillOpacity);
  const polyStyle   = s.hasFill ? PolygonType.StrokeFill : PolygonType.Stroke;
  const dashedValue = [4, 4];
  return {
    line:    { color: s.lineColor, size: s.lineWidth, style: s.lineStyle, dashedValue },
    polygon: { style: polyStyle, color: fillRgba, borderColor: s.borderColor, borderSize: s.borderWidth, borderStyle: s.borderStyle, borderDashedValue: dashedValue },
    rect:    { style: polyStyle, color: fillRgba, borderColor: s.borderColor, borderSize: s.borderWidth, borderStyle: s.borderStyle, borderDashedValue: dashedValue },
    circle:  { style: polyStyle, color: fillRgba, borderColor: s.borderColor, borderSize: s.borderWidth, borderStyle: s.borderStyle, borderDashedValue: dashedValue },
    arc:     { color: s.lineColor, size: s.lineWidth, style: s.lineStyle, dashedValue },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  overlayId: string;
  onClose:   () => void;
}

export function OverlayStyleModal({ overlayId, onClose }: Props) {
  const chart = useChartStore((s) => s.chart);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartExt = chart as any;

  const overlay        = chartExt?.getOverlayById(overlayId) ?? null;
  const overlayName    = overlay?.name as string | undefined;
  const existingStyles = overlay?.styles as DeepPartial<OverlayStyle> | null | undefined;

  const originalStylesRef = useRef<DeepPartial<OverlayStyle> | null>(existingStyles ?? null);

  const [lineColor,   setLineColor]   = useState<string>  (() => colorToHex(existingStyles?.line?.color));
  const [lineWidth,   setLineWidth]   = useState<number>  (() => existingStyles?.line?.size ?? 1);
  const [lineStyle,   setLineStyle]   = useState<LineType>(() => existingStyles?.line?.style ?? LineType.Solid);
  const [hasFill,     setHasFill]     = useState<boolean> (() => {
    const ps = existingStyles?.polygon?.style;
    return ps === PolygonType.Fill || ps === PolygonType.StrokeFill;
  });
  const [fillColor,   setFillColor]   = useState<string>  (() => colorToHex(existingStyles?.polygon?.color));
  const [fillOpacity, setFillOpacity] = useState<number>  (() => colorToOpacity(existingStyles?.polygon?.color));
  const [borderColor, setBorderColor] = useState<string>  (() => colorToHex(existingStyles?.polygon?.borderColor));
  const [borderWidth, setBorderWidth] = useState<number>  (() => existingStyles?.polygon?.borderSize ?? 1);
  const [borderStyle, setBorderStyle] = useState<LineType>(() => existingStyles?.polygon?.borderStyle ?? LineType.Solid);

  function snap(): StyleState {
    return { lineColor, lineWidth, lineStyle, hasFill, fillColor, fillOpacity, borderColor, borderWidth, borderStyle };
  }

  function applyToChart(s: StyleState) {
    chartExt?.overrideOverlay({ id: overlayId, styles: buildOverlayStyle(s) } as Partial<OverlayCreate>);
  }

  function changeLineColor(v: string)     { setLineColor(v);   applyToChart({ ...snap(), lineColor: v });   }
  function changeLineWidth(v: number)     { setLineWidth(v);   applyToChart({ ...snap(), lineWidth: v });   }
  function changeLineStyle(v: LineType)   { setLineStyle(v);   applyToChart({ ...snap(), lineStyle: v });   }
  function changeHasFill(v: boolean)      { setHasFill(v);     applyToChart({ ...snap(), hasFill: v });     }
  function changeFillColor(v: string)     { setFillColor(v);   applyToChart({ ...snap(), fillColor: v });   }
  function changeFillOpacity(v: number)   { setFillOpacity(v); applyToChart({ ...snap(), fillOpacity: v }); }
  function changeBorderColor(v: string)   { setBorderColor(v); applyToChart({ ...snap(), borderColor: v }); }
  function changeBorderWidth(v: number)   { setBorderWidth(v); applyToChart({ ...snap(), borderWidth: v }); }
  function changeBorderStyle(v: LineType) { setBorderStyle(v); applyToChart({ ...snap(), borderStyle: v }); }

  function handleRevertToDefault() {
    const defaults: StyleState = {
      lineColor:   DEFAULT_COLOR, lineWidth:   1, lineStyle:   LineType.Solid,
      hasFill:     false,
      fillColor:   DEFAULT_COLOR, fillOpacity: 20,
      borderColor: DEFAULT_COLOR, borderWidth: 1, borderStyle: LineType.Solid,
    };
    applyToChart(defaults);
    setLineColor(DEFAULT_COLOR); setLineWidth(1);  setLineStyle(LineType.Solid);
    setHasFill(false);
    setFillColor(DEFAULT_COLOR); setFillOpacity(20);
    setBorderColor(DEFAULT_COLOR); setBorderWidth(1); setBorderStyle(LineType.Solid);
  }

  function handleCancel() {
    chartExt?.overrideOverlay({ id: overlayId, styles: originalStylesRef.current } as Partial<OverlayCreate>);
    onClose();
  }

  const displayName = overlayName
    ? overlayName.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim()
    : 'Drawing';

  const labelCls   = 'text-sm font-semibold uppercase tracking-wider text-modal-muted mb-2';
  const rowCls     = 'flex items-center justify-between text-sm text-modal-text gap-3';
  const sectionCls = 'flex flex-col gap-2';

  function StyleToggle({ value, current, onChange, children }: {
    value: LineType; current: LineType; onChange: (v: LineType) => void; children: React.ReactNode;
  }) {
    const active = value === current;
    return (
      <button
        onClick={() => onChange(value)}
        className={`px-2.5 py-1 rounded text-sm border transition-colors ${
          active
            ? 'border-accent bg-accent/20 text-accent'
            : 'border-border text-modal-muted hover:text-modal-text hover:border-accent/60'
        }`}
      >
        {children}
      </button>
    );
  }

  return (
    <Modal onClose={handleCancel} size="sm" zIndex={Z_INDEX.topmost}>
      <Modal.Header onClose={handleCancel} title={`${displayName} Properties`} />

      <Modal.Body className="max-h-[70vh]">

          {/* LINE */}
          <div className={sectionCls}>
            <p className={labelCls}>Line</p>
            <div className={rowCls}>
              <span>Color</span>
              <ColorSwatch value={lineColor} onChange={changeLineColor} />
            </div>
            <div className={rowCls}>
              <span>Width&ensp;<b className="text-accent">{lineWidth}px</b></span>
              <input type="range" min={1} max={10} step={1} value={lineWidth}
                onChange={(e) => changeLineWidth(Number(e.target.value))}
                className="w-32 accent-[var(--color-accent)]" />
            </div>
            <div className={rowCls}>
              <span>Style</span>
              <div className="flex gap-1">
                <StyleToggle value={LineType.Solid}  current={lineStyle} onChange={changeLineStyle}>Solid</StyleToggle>
                <StyleToggle value={LineType.Dashed} current={lineStyle} onChange={changeLineStyle}>Dashed</StyleToggle>
              </div>
            </div>
          </div>

          {/* FILL */}
          <div className={sectionCls}>
            <p className={labelCls}>Fill</p>
            <div className={rowCls}>
              <span>Enable fill</span>
              <input type="checkbox" checked={hasFill}
                onChange={(e) => changeHasFill(e.target.checked)}
                className="rounded border-border accent-[var(--color-accent)] h-4 w-4 cursor-pointer" />
            </div>
            {hasFill && (
              <>
                <div className={rowCls}>
                  <span>Color</span>
                  <ColorSwatch value={fillColor} onChange={changeFillColor} />
                </div>
                <div className={rowCls}>
                  <span>Opacity&ensp;<b className="text-accent">{fillOpacity}%</b></span>
                  <input type="range" min={0} max={100} step={5} value={fillOpacity}
                    onChange={(e) => changeFillOpacity(Number(e.target.value))}
                    className="w-32 accent-[var(--color-accent)]" />
                </div>
              </>
            )}
          </div>

          {/* BORDER */}
          <div className={sectionCls}>
            <p className={labelCls}>Border</p>
            <div className={rowCls}>
              <span>Color</span>
              <ColorSwatch value={borderColor} onChange={changeBorderColor} />
            </div>
            <div className={rowCls}>
              <span>Width&ensp;<b className="text-accent">{borderWidth}px</b></span>
              <input type="range" min={1} max={10} step={1} value={borderWidth}
                onChange={(e) => changeBorderWidth(Number(e.target.value))}
                className="w-32 accent-[var(--color-accent)]" />
            </div>
            <div className={rowCls}>
              <span>Style</span>
              <div className="flex gap-1">
                <StyleToggle value={LineType.Solid}  current={borderStyle} onChange={changeBorderStyle}>Solid</StyleToggle>
                <StyleToggle value={LineType.Dashed} current={borderStyle} onChange={changeBorderStyle}>Dashed</StyleToggle>
              </div>
            </div>
          </div>
      </Modal.Body>

      <Modal.Footer className="!justify-between">
        <button onClick={handleRevertToDefault}
          className="text-sm px-3 py-1.5 rounded-lg border border-border text-modal-muted hover:text-modal-text hover:border-accent hover:bg-modal-hover transition-colors">
          Revert to default
        </button>
        <div className="flex gap-2">
          <button onClick={handleCancel}
            className="text-sm px-3 py-1.5 rounded-lg border border-border text-modal-muted hover:text-modal-text hover:bg-modal-hover transition-colors">
            Cancel
          </button>
          <button onClick={onClose}
            className="text-sm px-4 py-1.5 rounded-lg bg-accent text-white hover:bg-accent/80 transition-colors font-medium">
            Save
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function ColorSwatch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="relative cursor-pointer inline-block">
      <span
        className="block w-7 h-7 rounded-lg border-2 border-border hover:border-accent transition-colors shadow-sm"
        style={{ backgroundColor: value }}
      />
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
      />
    </label>
  );
}
